import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAskEntity, useDrawerPresence } from '../../shell/ShellContext'
import { dataService, type ShopifyCustomer, type ShopifyProduct } from '../../services/dataService'
import { productKey, recordName } from './knowledgeIndex'

/**
 * Which record the Knowledge drawer has open, and the Ask pin that goes with it. Moved out
 * of the old `screens/Memory.tsx` (MAR-325) with its rules intact, and reduced to ONE slot
 * for the one drawer (MAR-326).
 *
 * ── The URL is the source of truth ──────────────────────────────────────────────────────
 *
 * `?customer=<id>` or `?product=<id>` — never both: every write removes the other, so
 * opening a product replaces an open customer. Writes MERGE into the existing params with
 * `replace: true` (the Ledger's `?decision=` pattern, `screens/Ledger.tsx`), so `?view` and
 * `?scope` survive and opening a drawer does not stack history entries. A cold deep link
 * has only the id; the slot is built from it and the record is read. Back/Forward that
 * change the param change the drawer, through the same sync.
 *
 * The slot holds what an OPENER knew and the URL cannot carry: the index row, the label a
 * least-sure row verified, the full record once it is read. A slot whose (subject, id) the
 * URL no longer names is gone; one the URL names but no slot holds is opened cold.
 *
 * ── The pin ─────────────────────────────────────────────────────────────────────────────
 *
 * The shell is told which record is open, so the Ask dock can pin to it. One drawer, one
 * pin: opening the other subject swaps it, and every release is scoped to the record doing
 * the closing. Leaving Knowledge with the drawer open must not leave the dock claiming to
 * read a record no screen is showing — the unmount release covers that. "Ask" opens the dock
 * IN PLACE (`useOpenAsk`), so there is no hand-off that outlives its drawer and no exemption.
 *
 * ── A drawer belongs to the workspace it was opened from ────────────────────────────────
 *
 * ⚠️ A workspace switch does not close the drawer (MAR-205) and nothing else clears it, so
 * a product drawer used to survive into workspace B holding A's record while
 * `ProductKnowledge`, handed the LIVE workspace, re-read B's route for A's id, took the 404
 * and said *"She has not built an expert card for this product yet"* about a product that
 * has a card in A. So the shown drawer is DERIVED AT THE READ from the workspace it was
 * opened from — masked on the first render after the switch, before any effect has run.
 *
 * ⚠️ Masked, not erased: A → B → A brings the drawer back (MAR-205). That is why this is NOT
 * the Ledger's close-on-switch, and why the URL keeps its param under B: the sync below
 * leaves a slot alone whenever the URL still names it, whatever workspace is on screen. And
 * the RELEASES still match on the STORED record, never on the masked value — under B the
 * masked read is null, so a release matched against it would match nothing and the pin
 * would outlive its drawer.
 */

export type DrawerSubject = 'customer' | 'product'

/**
 * What the customer drawer may say about the person before — or without — their record.
 *
 * ⚠️ An opener carries only what it truly holds. A search row is a record from the index;
 * a least-sure row is a LABEL (the name and masked email the least-sure read verified), and
 * dressing it as a `ShopifyCustomer` once rendered "New · 0 orders" and "£0 lifetime" about
 * a person the page had read nothing of. So the header text and stat tiles render from a
 * RECORD — `full` (what `getCustomerById` returned, or a row already carrying the
 * intelligence blobs) or else `row` (the index row the drawer was opened from, whose list
 * facts are real and stay on screen while the full read loads, and if it fails). A label
 * opener has neither until the full read lands. The intelligence sections read `full` only.
 */
export interface CustomerOpening {
  customerId: string
  /**
   * The header name — the label the opener verified, never derived from an address. `null`
   * when the opener holds no name (a nameless row): the drawer then names the person from
   * the belief read, else the id.
   */
  name: string | null
  /** Shown under the name until the record lands. Masked when the opener was a label. */
  email?: string
}

export type CustomerDetail = 'ready' | 'loading' | 'failed'

export interface ShownCustomer {
  subject: 'customer'
  customerId: string
  /**
   * The name the opener verified, or `null` for a cold deep link, which holds only the id.
   * The drawer then names the person from the record, else the belief read's `person`, else
   * the id — never from an address.
   */
  name: string | null
  email?: string
  /** The full record, or `null` until (unless) it lands. Never the opener dressed up. */
  full: ShopifyCustomer | null
  /** The index row it was opened from — a real record — or `null` for a label or link. */
  row: ShopifyCustomer | null
  detail: CustomerDetail
}

export interface ShownProduct {
  subject: 'product'
  productId: string
  /**
   * The catalogue row it was opened from, or `null` for a least-sure row or a deep link.
   * ⚠️ Never a row built from a label: the old `{ productId, title } as ShopifyProduct`
   * rendered "— variants" and "— in stock" about a product the page had not read.
   */
  row: ShopifyProduct | null
  /** The title the opener held (row or least-sure label), or `null` for a deep link. */
  title: string | null
}

export type ShownDrawer = ShownCustomer | ShownProduct

/** The slot: what is shown, plus the workspace it was opened from — masked under any other. */
type Slot = ShownDrawer & { workspaceId: string | undefined }

const slotId = (s: ShownDrawer) => (s.subject === 'customer' ? s.customerId : s.productId)

/** What an open asked the URL for. `'closed'` is a close waiting for its param to go. */
type Intent = { subject: DrawerSubject; id: string } | 'closed'

export function useKnowledgeDrawers(workspaceId: string | undefined, accountId: string | undefined) {
  const { setEntity: setAskEntity, release: releaseAskEntity } = useAskEntity()
  const [searchParams, setSearchParams] = useSearchParams()
  const releaseRef = useRef<() => void>(() => {})
  /**
   * ⚠️ Newest open wins. Every open (and close, and retry) bumps this, and a detail fetch
   * writes only if it is still the newest — otherwise opening A then B before A's
   * `getCustomerById` returns lands A's full record in the drawer that is showing B (the
   * old screen's race).
   */
  const customerRequest = useRef(0)
  /**
   * What the last open or close asked the URL for, until the URL says it.
   *
   * ⚠️ An opener writes the slot and the param in one act, and React normally commits both
   * in one render. If a router ever splits them (a transition), the sync would see a slot
   * the URL does not name yet and close it — or, on a close, a param with no slot and open
   * it cold again. The sync waits while the URL has not caught up with this.
   */
  const intent = useRef<Intent | null>(null)

  const [slot, setSlot] = useState<Slot | null>(null)

  // The URL's ask. `customer` wins if a hand-written link carries both; no write of ours does.
  const paramCustomer = searchParams.get('customer')
  const paramProduct = searchParams.get('product')
  const wantedSubject: DrawerSubject | null = paramCustomer ? 'customer' : paramProduct ? 'product' : null
  const wantedId = paramCustomer || paramProduct || null
  /** The workspace the link names, or `null` for a hand-written link, which opens in the live one. */
  const wantedWs = searchParams.get('ws')

  // `undefined === undefined` is a deliberate match: a drawer opened with no workspace and
  // still under no workspace is under the same (absent) one, and neither knowledge component
  // reads anything without an id anyway.
  let shown: ShownDrawer | null = null
  if (slot && slot.workspaceId === workspaceId) {
    const { workspaceId: _ws, ...rest } = slot
    void _ws
    shown = rest
  }

  // ⚠️ The SHOWN drawer: one scoped out of this workspace is not on screen, so it must not
  // hold the dock collapsed for a panel the merchant cannot see.
  // …and held until its slide-out ends, so the dock does not re-expand under a closing panel.
  const onExited = useDrawerPresence(!!shown)

  // Re-armed on every render so the unmount cleanup sees the drawer as it is.
  useEffect(() => {
    releaseRef.current = () => {
      if (slot) releaseAskEntity(slot.subject, slotId(slot))
    }
  })
  useEffect(
    () => () => {
      // Bump first, so a cold link's `getCustomerById` still in flight lands as a no-op. (It
      // cannot re-pin either way any more: the re-pin is the effect below, and an unmounted
      // hook runs no effects.)
      customerRequest.current++
      releaseRef.current()
    },
    []
  )

  /**
   * Write the param: one subject or none, merged into what the URL already says — and the
   * workspace it belongs to, `ws`, beside it. ⚠️ `ws` is what keeps a masked drawer's link
   * from becoming a cold open of A's id under B on a reload or a shared link (reading B's
   * routes for A's record, and offering erasure in B for it). It goes with them on close.
   */
  const setParam = useCallback(
    (subject: DrawerSubject | null, id?: string, ws?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('customer')
          next.delete('product')
          next.delete('ws')
          if (subject && id) {
            next.set(subject, id)
            if (ws) next.set('ws', ws)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  /** Fetch the full record for the open drawer, writing only while it is still the newest. */
  const readDetail = useCallback(
    async(customerId: string, ws: string | undefined) => {
      const mine = ++customerRequest.current
      setSlot((cur) =>
        cur && cur.subject === 'customer' && cur.customerId === customerId ? { ...cur, detail: 'loading' } : cur
      )
      let full: ShopifyCustomer | null = null
      try {
        full = await dataService.getCustomerById({ accountId: accountId || '', workspaceId: ws || '', customerId })
      } catch {
        full = null
      }
      // A newer open (or a close) has superseded this one: its answer describes someone the
      // drawer is no longer showing, and its `detail` would end the NEWER read's spinner.
      if (mine !== customerRequest.current) return
      // ⚠️ A null or thrown read is `failed`, with a retry — never the opener's zeros.
      setSlot((cur) =>
        cur && cur.subject === 'customer' && cur.customerId === customerId
          ? { ...cur, full: full || null, detail: full ? 'ready' : 'failed' }
          : cur
      )
      // The pin is NOT written here — see the effect below. It is the record's arrival in a
      // SHOWN drawer that renames the pin, not the read's arrival.
    },
    [accountId]
  )

  /**
   * THE pin write — the only one. Whatever drawer is on screen is pinned, once per showing,
   * labelled from what the slot holds: a person's verified `name`, else their record's NAME
   * (`recordName` — never the address a nameless record would fall back to), else the id; a
   * product's `title` (the opener's, or the card's via `relabel`), else "Product <id>".
   *
   * ⚠️ An effect on the SHOWN drawer, never a write from an opener or a read. A read that
   * lands while the drawer is masked under B must not write: the shell holds ONE pinned
   * entity, and A's record would overwrite whatever B had pinned. And on the return to A the
   * drawer back on screen must be pinned again — B may have pinned something else meanwhile,
   * and the restore used to re-assert only a cold-opened customer (PR #257 reviews). So:
   * `pinned` keeps it to once per (workspace, record, label), and is cleared while nothing
   * is shown, so every showing pins. Releases stay where they were — close, swap, the param
   * going, unmount — each matched against the stored record.
   *
   * An id-less row (see `place`) is never pinned: the dock could never resolve it.
   */
  const pinned = useRef<string | null>(null)
  useEffect(() => {
    if (!shown || !workspaceId) {
      pinned.current = null
      return
    }
    const id = slotId(shown)
    if (!id) return
    const label =
      shown.subject === 'customer'
        ? shown.name ?? recordName(shown.full) ?? id
        : shown.title || `Product ${id}`
    const key = `${workspaceId}::${shown.subject}::${id}::${label}`
    if (pinned.current === key) return
    pinned.current = key
    setAskEntity({ type: shown.subject, id, label, workspaceId })
  })

  /**
   * Put a record in the slot and ask the URL for it. The previous record's pin is released
   * first — one drawer, one pin, and the release is scoped to what was held. The new record
   * is pinned by the effect above once it is on screen.
   *
   * ⚠️ An id-less row (a data fault: `customerId` is typed `string`, not guaranteed) still
   * opens — its row's facts are real — but the URL cannot name it and the dock could never
   * resolve it, so it gets neither a param nor a pin. Any param for the record it replaces
   * is cleared, or the sync would put that record straight back.
   */
  const place = useCallback(
    (next: Slot, writeUrl: boolean) => {
      if (slot) releaseAskEntity(slot.subject, slotId(slot))
      customerRequest.current++
      pinned.current = null
      setSlot(next)
      const id = slotId(next)
      if (writeUrl) {
        if (id) {
          intent.current = { subject: next.subject, id }
          setParam(next.subject, id, next.workspaceId)
        } else if (wantedSubject) {
          intent.current = 'closed'
          setParam(null)
        }
      }
    },
    [slot, wantedSubject, releaseAskEntity, setParam]
  )

  // ── The sync: the URL decides; the slot follows. ──
  useEffect(() => {
    const pending = intent.current
    if (pending) {
      const caughtUp =
        pending === 'closed'
          ? wantedSubject == null
          : pending.subject === wantedSubject && pending.id === wantedId
      if (!caughtUp) return
      intent.current = null
    }

    if (!wantedSubject || !wantedId) {
      // The param went (Back, or a link without it): the drawer goes with it — unless it is
      // an id-less row, which never had a param to lose (see `place`).
      if (slot && slotId(slot)) {
        customerRequest.current++
        releaseAskEntity(slot.subject, slotId(slot))
        setSlot(null)
      }
      return
    }
    // ⚠️ The URL still names the slot: leave it — EVEN under another workspace. That is the
    // mask-and-restore (MAR-205); replacing it here would be the Ledger's close-on-switch.
    // A slot named by id but in another workspace than the link's `ws` is NOT named.
    if (
      slot &&
      slot.subject === wantedSubject &&
      slotId(slot) === wantedId &&
      (!wantedWs || slot.workspaceId === wantedWs)
    ) {
      return
    }
    // A cold link before the workspace is known waits: a slot stamped with no workspace
    // would be masked for good the moment the real one arrived. And ⚠️ a link to ANOTHER
    // workspace's record opens nothing here — no slot, no read, no pin — and its param stays,
    // so returning to that workspace opens it there. Opening it would read this workspace's
    // routes for that workspace's id and offer erasure in the wrong one.
    const opensHere = !!workspaceId && (!wantedWs || wantedWs === workspaceId)
    if (!opensHere) {
      // The URL moved to a record that does not open here: the drawer it held goes, and its
      // pin with it. When it DOES open here, `place` below owns that release — one path for
      // "replace the slot", rather than releasing here and again through `place`'s closure.
      if (slot) {
        customerRequest.current++
        releaseAskEntity(slot.subject, slotId(slot))
        setSlot(null)
      }
      return
    }

    // ⚠️ A link that arrived WITHOUT `ws` (hand-written, or one from before `ws` existed) is
    // stamped with the workspace it opened in. Otherwise its param stays unscoped: masked
    // under B, then reloaded or shared there, it would cold-open A's id under B — the very
    // case `ws` exists to stop (PR #257 review). `place` writes it with `replace`, through
    // `intent`, exactly as an opener does.
    const stampWs = !wantedWs

    // Opened from the URL alone — a deep link, or Back/Forward to another record.
    if (wantedSubject === 'customer') {
      place(
        { subject: 'customer', workspaceId, customerId: wantedId, name: null, full: null, row: null, detail: 'loading' },
        stampWs
      )
      void readDetail(wantedId, workspaceId)
    } else {
      place({ subject: 'product', workspaceId, productId: wantedId, row: null, title: null }, stampWs)
    }
  }, [wantedSubject, wantedId, wantedWs, slot, workspaceId, place, readDetail, releaseAskEntity])

  /**
   * Open a person.
   *
   * ⚠️ `name` is display text and nothing more — it can be an email address. The model's
   * `[VIEWING: …]` line is built server-side, never from it. `customerId` is typed
   * `string`, which is why the runtime check is in `place`: an id-less row opens with no
   * param and no pin, because the URL cannot name it and the dock could never resolve it.
   *
   * `record` is the full record when the opener already holds one (a row carrying the
   * intelligence blobs). List queries omit those, so otherwise it is fetched on open.
   */
  const open = useCallback(
    (opening: CustomerOpening, record: ShopifyCustomer | null, row: ShopifyCustomer | null) => {
      place(
        {
          subject: 'customer',
          workspaceId,
          customerId: opening.customerId,
          name: opening.name,
          email: opening.email,
          full: record,
          row,
          detail: record ? 'ready' : 'loading',
        },
        true
      )
      if (record) return Promise.resolve()
      return readDetail(opening.customerId, workspaceId)
    },
    [workspaceId, place, readDetail]
  )

  /**
   * Open a person from an index row: a record, whose own NAME heads the drawer. ⚠️
   * `recordName`, not `custName` — a nameless row's `custName` is its real email address,
   * which would head the drawer, fill "Ask about …" and label the Ask pin.
   */
  const openCustomer = useCallback(
    (c: ShopifyCustomer) =>
      open({ customerId: c.customerId, name: recordName(c), email: c.email }, c.customerIntelligence ? c : null, c),
    [open]
  )

  /** Open a person from a label (a least-sure row): only the name and masked email it carries. */
  const openCustomerLabel = useCallback((opening: CustomerOpening) => open(opening, null, null), [open])

  /** Read the open drawer's record again, after a failure. */
  const retryCustomer = useCallback(() => {
    if (!slot || slot.subject !== 'customer' || slot.workspaceId !== workspaceId) return
    void readDetail(slot.customerId, slot.workspaceId)
  }, [slot, workspaceId, readDetail])

  /**
   * Open a product. Synchronous, so there is no response here to race: the only async read
   * behind the product drawer is `ProductKnowledge`'s card read, which already keeps the
   * newest request per (workspace, product) and drops a late one (`requestSeq` +
   * `stillCurrent` in `useBeliefRead`, pinned by `KnowledgeIndex.test.tsx`). ⚠️ No detail
   * fetch: the card comes from `knowledge/products/{id}`, which reads the expert-card
   * partition keyed on the VERIFIED account and workspace, rather than `shopify-products`,
   * whose `PRODUCT#<id>` key carries no tenant for its handler to enforce.
   */
  const openProduct = useCallback(
    (p: ShopifyProduct) => {
      place(
        { subject: 'product', workspaceId, productId: productKey(p) || '', row: p, title: p.title || null },
        true
      )
    },
    [workspaceId, place]
  )

  /**
   * Open a product from a label (a least-sure row): the id and the title the read verified,
   * and nothing else — so the drawer shows no catalogue tiles rather than "—" for them.
   */
  const openProductLabel = useCallback(
    (label: { productId: string; title: string | null }) => {
      if (!label.productId) return
      place(
        { subject: 'product', workspaceId, productId: label.productId, row: null, title: label.title },
        true
      )
    },
    [workspaceId, place]
  )

  /**
   * A record named itself after it opened — a product opened cold or from a least-sure label
   * has only "Product <id>" until its card names it. The name goes INTO THE SLOT, and the pin
   * effect relabels from there, so the label also survives a mask and restore. ⚠️ Only when
   * the slot IS that record, in the LIVE workspace: a late card for a record the merchant has
   * left, or one masked under another workspace, changes nothing.
   */
  const relabel = useCallback(
    (subject: DrawerSubject, id: string, label: string) => {
      if (!slot || !workspaceId || slot.workspaceId !== workspaceId) return
      if (slot.subject !== subject || slotId(slot) !== id || !id || !label) return
      setSlot((cur) => {
        if (!cur || cur.subject !== subject || slotId(cur) !== id || cur.workspaceId !== workspaceId) return cur
        return cur.subject === 'product' ? { ...cur, title: label } : { ...cur, name: label }
      })
    },
    [slot, workspaceId]
  )

  /** Close the drawer. With no param and no slot there is nothing to close, and nothing is written. */
  const close = useCallback(() => {
    if (!slot && !wantedSubject) return
    customerRequest.current++
    if (slot) releaseAskEntity(slot.subject, slotId(slot))
    setSlot(null)
    if (wantedSubject) {
      intent.current = 'closed'
      setParam(null)
    }
  }, [slot, wantedSubject, releaseAskEntity, setParam])

  return {
    shown,
    openCustomer,
    openCustomerLabel,
    openProduct,
    openProductLabel,
    retryCustomer,
    relabel,
    close,
    onExited,
  }
}
